import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { RefreshTokenRequest, RefreshTokenResponse } from '../types';
import { API_ENDPOINTS, TIMEOUTS, STORAGE_KEYS, HEADERS, MIME_TYPES } from '../constants/api';

export const API_BASE_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  ? 'http://localhost:5000/api/v1'
  : 'https://api.dealtochka.com/api/v1';


// Интерфейс для очереди запросов при обновлении токена
interface QueueItem {
  resolve: (token: string) => void;
  reject: (err: unknown) => void;
}

// Расширяем AxiosRequestConfig для поддержки _retry
interface ExtendedAxiosRequestConfig extends AxiosRequestConfig {
  _retry?: boolean;
  headers?: any;
}

// Состояние обновления токена
let isRefreshing = false;
const failedQueue: QueueItem[] = [];

// Функция для обработки очереди запросов
const processQueue = (error: unknown, token: string | null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token!);
    }
  });
  
  failedQueue.length = 0;
};

// Функция для разлогина пользователя
export const logoutUser = () => {
  localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.USER_DATA);
  
  // Редирект на страницу логина
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

// Создание экземпляра axios
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: TIMEOUTS.REQUEST,
  headers: {
    [HEADERS.CONTENT_TYPE]: MIME_TYPES.JSON,
  },
});

// Request interceptor to add token to all requests
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    if (token && config.headers) {
      config.headers[HEADERS.AUTHORIZATION] = `Bearer ${token}`;
    }
    return config;
  },
  (error: unknown) => {
    return Promise.reject(error);
  }
);

// Response interceptor for automatic token refresh
api.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: any) => {
    const originalRequest = error.config as ExtendedAxiosRequestConfig;

    // Если 401 и не пробовали уже обновить токен
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token: string) => {
              if (originalRequest.headers) {
                originalRequest.headers[HEADERS.AUTHORIZATION] = `Bearer ${token}`;
              }
              resolve(api(originalRequest));
            },
            reject: (err: unknown) => reject(err),
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshData: RefreshTokenRequest = {
          accessToken: localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) || '',
          refreshToken: localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || ''
        };

        const response = await axios.post<RefreshTokenResponse>(
          API_BASE_URL + API_ENDPOINTS.AUTH.REFRESH, 
          refreshData
        );

        if (response.data.result === 'Authorized') {
          localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, response.data.accessToken!);
          localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, response.data.refreshToken!);

          if (api.defaults.headers.common) {
            api.defaults.headers.common[HEADERS.AUTHORIZATION] = `Bearer ${localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN)}`;
          }
          processQueue(null, localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN));
        } else {
          // Если refresh не удался, разлогиниваем пользователя
          logoutUser();
          processQueue(new Error('Token refresh failed'), null);
          return Promise.reject(new Error('Token refresh failed'));
        }
        return api(originalRequest);
      } catch (err) {
        // Если произошла ошибка при обновлении токена, разлогиниваем пользователя
        logoutUser();
        processQueue(err, null);
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;
